// Building blocks every S1-16 screen shares, so sections, loading and error states look the
// same everywhere (one design system: these only compose @companyos/ui primitives).

import { ApiError } from "@companyos/sdk";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
} from "@companyos/ui";
import type { ReactNode } from "react";
import { useFocus } from "../focus/focus";
import { currentWeek } from "../focus/weeks";

/** The focused week (`p`), or this week. */
export function usePeriod(): string {
  const { focus } = useFocus();
  return focus.period ?? currentWeek();
}

/** A titled region: `<section aria-labelledby>` with an h2, optional actions on the right. */
export function Section({
  id,
  title,
  description,
  actions,
  children,
}: {
  id: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="mt-8 flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 id={id} className="font-heading text-base font-semibold">
            {title}
          </h2>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

/** Placeholder rows while a query loads (announced politely, not as content). */
export function Loading({ label, rows = 3 }: { label: string; rows?: number }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}

/** Inline failure state (the toast already carries the problem detail). */
export function LoadError({ what, error }: { what: string; error: unknown }) {
  const detail =
    error instanceof ApiError
      ? (error.problem?.detail ?? error.problem?.title ?? error.message)
      : error instanceof Error
        ? error.message
        : "Unexpected error.";
  return (
    <Alert variant="destructive" role="alert">
      <AlertTitle>{what} could not be loaded</AlertTitle>
      <AlertDescription>{detail}</AlertDescription>
    </Alert>
  );
}

export function Nothing({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Empty className="border border-dashed border-border">
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        {children ? <EmptyDescription>{children}</EmptyDescription> : null}
      </EmptyHeader>
    </Empty>
  );
}

/**
 * Coverage of a score (domain-kpi completeness). The band is shown from the 0.6 cutoff up, so
 * a band on a week that is not complete is provisional and says how much data it rests on.
 */
export function coverageText(completeness: number, band: string): string {
  const pct = `${Math.round(completeness * 100)}%`;
  if (band === "insufficient_data") return `Only ${pct} of the data is in: no band yet`;
  return completeness < 1 ? `Provisional: ${pct} of the data is in` : "All data in";
}

/** Marks a band that rests on incomplete data (shown beside the band badge). */
export function ProvisionalBadge({ completeness, band }: { completeness: number; band: string }) {
  if (completeness >= 1 || band === "insufficient_data") return null;
  return (
    <Badge
      variant="outline"
      data-slot="provisional"
      title={coverageText(completeness, band)}
      aria-label={coverageText(completeness, band)}
    >
      Provisional
    </Badge>
  );
}

/** "Load more" for a cursor-paginated list: further pages load on request only. */
export function LoadMore({
  what,
  hasMore,
  loading,
  onMore,
}: {
  what: string;
  hasMore: boolean;
  loading: boolean;
  onMore: () => void;
}) {
  if (!hasMore) return null;
  return (
    <Button
      variant="outline"
      className="self-start max-md:h-11"
      disabled={loading}
      onClick={onMore}
    >
      {loading ? "Loading…" : `Load more ${what}`}
    </Button>
  );
}

/** Label/value pairs as a description list. */
export function Facts({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <div key={item.label} className="flex min-w-0 flex-col gap-0.5">
          <dt className="text-muted-foreground">{item.label}</dt>
          <dd className="min-w-0 break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
